import React from 'react';
import ExecPricingView from 'views/ExecPricingView';
import { Section } from 'shared/ui/Section';
import { Btn } from 'shared/ui/Btn';
import { DataTable, type Column } from 'shared/ui/DataTable';
import { formatMoney } from 'shared/lib/format';
import { useQuoteActionsCtx } from 'shared/state/quoteActionsContext';
import type { BomRow } from 'shared/state/types';
import { useBomSection } from './useBomSection';

const money = (v: unknown) => (v === undefined ? '' : formatMoney(Number(v)));

function buildColumns(showDiscounts: boolean): Column<BomRow>[] {
  const base: Column<BomRow>[] = [
    { key: 'pn', header: 'P/N', render: (r) => r.partNumber ?? '' },
    { key: 'desc', header: 'Description', render: (r) => r.item },
  ];
  const discountsLeft: Column<BomRow>[] = showDiscounts ? [{ key: 'node', header: 'Node', render: (r) => r.node ?? '' }] : [];
  const mid: Column<BomRow>[] = [
    { key: 'qty', header: 'Qty', align: 'right', render: (r) => r.quantity ?? '' },
    { key: 'months', header: 'Months', align: 'right', render: (r) => r.months ?? '' },
    { key: 'list', header: 'List', align: 'right', render: (r) => money(r.list) },
  ];
  const discountsRight: Column<BomRow>[] = showDiscounts
    ? [
      { key: 'discount', header: 'Discount', align: 'right', render: (r) => r.discount ?? '' },
      { key: 'unit', header: 'Discounted Price', align: 'right', render: (r) => money(r.unitCost) },
    ]
    : [];
  return [...base, ...discountsLeft, ...mid, ...discountsRight,
    { key: 'ext', header: 'Extended', align: 'right', render: (r) => money(r.totalCost) }];
}

type TotalsRowProps = { id: string; name: string; value: number; span: number; strong?: boolean; custom?: boolean };

/** Hoisted to module scope (Ruling P11): a component redefined on every render
 *  of Totals would remount its subtree each time. */
function TotalsRow({ id, name, value, span, strong, custom }: TotalsRowProps) {
  // `custom` reddens the strong row, matching classic: the Solution Price figure
  // turns red whenever ANY bom row carries a custom price (App.js:1187), which
  // on an exec quote is the grand-total counterpart to the red BOM lines.
  const strongTone = custom ? 'font-bold text-danger' : 'font-bold text-brand-strong';
  return (
    <tr>
      <td colSpan={span - 1} className={`px-3 py-1 text-right font-body text-sm ${strong ? 'font-bold text-ink' : 'text-ink-muted'}`}>{name}</td>
      <td data-testid={id} className={`px-3 py-1 text-right font-code text-sm ${strong ? strongTone : 'text-ink'}`}>{formatMoney(value)}</td>
    </tr>
  );
}

function Totals({ span, software, hardware, grand, label, softwareOnly, hasCustomPrice }: {
  span: number; software: number; hardware: number; grand: number; label: string;
  softwareOnly: boolean; hasCustomPrice: boolean;
}) {
  return (
    <>
      <TotalsRow id="bom-software" name="Software" value={software} span={span} />
      {!softwareOnly && <TotalsRow id="bom-hardware" name="Hardware" value={hardware} span={span} />}
      <TotalsRow id="bom-grand" name={label} value={grand} span={span} strong custom={hasCustomPrice} />
    </>
  );
}

export function BomSection({ id }: { id: string }) {
  const s = useBomSection();
  const { startReprice } = useQuoteActionsCtx();
  const columns = buildColumns(s.canSeeDiscounts && s.showDiscountColumns);

  return (
    <Section.Root id={id} defaultOpen>
      <Section.Bar
        title="BOM"
        role="Bill of materials"
        summary={s.isCalculating ? '…' : s.summary}
        trailing={
          <span className="flex gap-2">
            {s.canSeeDiscounts && (
              <Btn variant="text" size="sm" onClick={s.toggleDiscountColumns}>
                {s.showDiscountColumns ? 'Hide discount columns' : 'Show discount columns'}
              </Btn>
            )}
            {s.canExec && (
              <Btn variant="outlined" size="sm" aria-expanded={s.execOpen} aria-controls="exec-pricing-panel" onClick={s.toggleExec}>
                Custom HW Pricing
              </Btn>
            )}
          </span>
        }
      />
      <Section.Body>
        {s.canExec && s.execOpen && (
          <div id="exec-pricing-panel" className="mb-4">
            <ExecPricingView
              execPricing={s.execPricing}
              productDb={s.priceBook}
              config={s.config}
              activePartNumbers={s.activePartNumbers}
              onReprice={startReprice}
            />
          </div>
        )}
        <DataTable
          caption="Bill of materials"
          columns={columns}
          rows={s.rows}
          getRowKey={(r, i) => `${r.partNumber ?? r.item}-${i}`}
          rowClassName={(r) => (r.isCustomPrice ? 'text-danger' : '')}
          isDivider={s.isSeparator}
          footer={<Totals span={columns.length} software={s.totals.software} hardware={s.totals.hardware}
            grand={s.totals.grand} label={s.solutionLabel} softwareOnly={s.softwareOnly} hasCustomPrice={s.hasCustomPrice} />}
        />
        {s.softwareOnly && s.hardwareReferenceRows.length > 0 && (
          <div className="mt-4">
            <h4 className="mb-2 font-display text-xs text-ink-muted">
              Hardware Reference: fulfilled by contract manufacturer
            </h4>
            <DataTable
              caption="Hardware reference"
              columns={[
                { key: 'node', header: 'Node Description', render: (r: any) => r.nodeDescription },
                { key: 'nodeQty', header: 'Node Qty', align: 'right', render: (r: any) => r.nodeQty },
                { key: 'cap', header: 'Device Capacity (TB)', align: 'right', render: (r: any) => r.deviceCapacityTB },
                { key: 'devQty', header: 'Device Qty', align: 'right', render: (r: any) => r.deviceQty },
                { key: 'raw', header: 'Total RAW (TB)', align: 'right', render: (r: any) => Number(r.totalRawTB).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
              ]}
              rows={s.hardwareReferenceRows}
              getRowKey={(_r, i) => String(i)}
            />
          </div>
        )}
      </Section.Body>
    </Section.Root>
  );
}
